<template>
	<header-block title="Money"></header-block>
	<CustomerLoadWait :customer="customer" />

	<transition>
		<div v-if="customer">

			<AwaitBankAccountPanel v-if="adding && bankInfo" :origCount="bankInfo.accounts.length" @accountAdded="adding=false; getBankInfo()" />

			<!-- Account Type Filter Skeleton -->
			<!-- <div v-if="!allAccountsLoaded" class="money-tabs">
				<v-skeleton-loader type="button" width="100%" height="50px" />
			</div> -->

			<!-- Account Type Filter (shown when user has accounts) -->
			<AccountTypeFilter
				v-if="allAccountsLoaded && hasAnyAccounts"
				:tabs="navigationTabs"
				v-model="selectedSection"
				v-model:subTabValue="selectedSubSection"
				class="money-tabs"
			/>

			<div class="money-page">
				<!-- Hero Carousel Skeleton Loader -->
				<div v-if="showWalkthrough() && (!heroWalkthroughData || !heroCarouselReady)" class="video-carousel-section">
					<v-skeleton-loader
						type="card"
						:height="determineCarouselHeight()"
					/>
				</div>

				<!-- Hero Carousel Section -->
				<div v-if="bankInfoLoaded && heroWalkthroughData && showWalkthrough() && heroCarouselReady" class="video-carousel-section">
					<MGWalkthrough
						:walkthroughData="heroWalkthroughData"
						variant="sheet"
						:compact="true"
						:dotsAbove="false"
						:imageHeight="'220'"
						:height="determineCarouselHeight()"
						@action-clicked="handleLinkAccount"
						@slide-index-changed="handleHeroSlideChange"
					/>
				</div>

				<!-- Content Section -->
				<div class="content-section">
					<!-- How it works button skeleton -->
					<div class="how-it-works-button" v-if="!allAccountsLoaded">
						<v-skeleton-loader type="button" height="60px" />
					</div>

					<div class="how-it-works-button" v-if="allAccountsLoaded && !hasBankAccounts && (!hasAnyAccounts || selectedSection === 'all')">
						<MGButton
							:fullWidth="true"
							:stacked="true"
							:outline="true"
							:noCaps="true"
							text="How it works"
							:arrow="true"
							@click="handleHowItWorks"
						/>
					</div>

					<div class="full-width-section">
						<!-- Account Cards Skeleton -->
						<div v-if="!allAccountsLoaded">
							<v-skeleton-loader type="card" height="180px" class="mb-4" />
							<v-skeleton-loader type="card" height="180px" class="mb-4" />
							<v-skeleton-loader type="card" height="180px" />
						</div>

						<AccountCardsSection
							v-if="allAccountsLoaded"
							:currentAccounts="currentAccounts"
							:currentAccountsBalance="currentAccountsBalance"
							:creditAccounts="creditAccounts"
							:creditAccountsBalance="creditAccountsBalance"
							:savingAccounts="savingAccounts"
							:savingAccountsBalance="savingAccountsBalance"
							:mortgageInfo="mortgageInfo"
							:insuranceInfo="insuranceInfo"
							:creditInfo="filteredCreditInfo"
							:investmentInfo="investmentInfo"
							:showCreditInfo="showCreditInfo"
							:showCurrentCard="showCurrentCard"
							:showCreditCard="showCreditCard"
							:showMortgageCard="showMortgageCard"
							:showInsuranceCard="showInsuranceCard"
							:showSavingCard="showSavingCard"
							:showPensionCard="showPensionCard"
							:showInvestmentCard="showInvestmentCard"
							:additionalLongTerm="additionalLongTerm"
							:additionalInsurance="additionalInsurance"
							:formatter="formatter"
							:selectedSection="selectedSection"
							:selectedSubSection="selectedSubSection"
							@filter-changed="handleFilterChanged"
							@mortgage-click="handleMortgageClick"
							@add-account-click="handleAddAccountClick"
						/>
					</div>

					<div class="full-width-section">
						<!-- Recent Transactions Section -->
						<RecentTransactionsSection
							v-if="allAccountsLoaded && hasBankAccounts && transactions && (selectedSection === 'all' || selectedSection === 'd2d')"
							:transactions="transactions"
							:formatter="formatter"
							:today="today"
							:yesterday="yesterday"
							:accountType="getRecentTransactionsAccountType"
							:accountIds="getAccountIdsByType(getRecentTransactionsAccountType)"
							@view-more="openTransactionsDrawer"
						/>
					</div>

					<!-- Documents Section -->
					<div class="full-width-section" v-if="allAccountsLoaded && selectedSection === 'docs'">
						<div class="documents-list" v-if="filteredDocuments && filteredDocuments.length > 0">
							<DocumentListItem
								v-for="doc in filteredDocuments"
								:key="`doc-${doc.id}`"
								:company="doc.company || doc.documentType || 'Document'"
								:fileName="doc.documentName"
								:date="doc.createDateTimeString || ''"
								:documentType="doc.sysoDocumentType?.name || doc.documentSubject || ''"
								@click="handleDocumentClick(doc)"
							/>
						</div>
						<div v-else class="empty-documents">
							<p class="empty-state-text">No documents yet</p>
							<p class="empty-state-subtext">Upload documents via WhatsApp and they'll appear here.</p>
						</div>

						<WhatsAppPromoCard
							ref="whatsAppPromoCard"
							class="whatsapp-promo-section"
							:showImage="true"
							:closable="false"
							title="Send a document or photo"
							subtitle="We'll add the key details and save the document securely.  You can also chat with your WhatsApp assistant about this policy."
							@action="handleWhatsAppAction"
						/>
					</div>

					<!-- What can I add section - only show when accounts are loaded -->
					<div v-if="allAccountsLoaded" @click="handleAddAccountClick()">
						<p class="what-can-add-heading">What can I add?</p>
						<p class="what-can-add-subtext">Link more accounts for more detailed analysis</p>
					</div>

					<!-- Account Type Grid Skeleton -->
					<div v-if="!allAccountsLoaded" class="account-type-grid-section">
						<v-row>
							<v-col cols="6">
								<v-skeleton-loader type="card" height="120px" />
							</v-col>
							<v-col cols="6">
								<v-skeleton-loader type="card" height="120px" />
							</v-col>
							<v-col cols="6">
								<v-skeleton-loader type="card" height="120px" />
							</v-col>
							<v-col cols="6">
								<v-skeleton-loader type="card" height="120px" />
							</v-col>
						</v-row>
					</div>

					<AccountTypeGrid v-if="allAccountsLoaded"
						class="account-type-grid-section"
						@account-type-click="handleStartWalkthroughClick"
						@add-account-click="handleAddAccountClick"
						@alt-action="handleAltAction"
						:currentAccounts="currentAccounts"
						:creditAccounts="creditAccounts"
						:savingAccounts="savingAccounts"
						:insuranceInfo="insuranceInfo"
						:mortgageInfo="mortgageInfo"
						:investmentInfo="investmentInfo"
						:creditInfo="filteredCreditInfo"
						:additionalLongTerm="additionalLongTerm"
						:additionalInsurance="additionalInsurance"
					/>				
					<FaqsSection
						class="faq-section"
						:faqs="faqs"
						:limit="5"
						faqName="money-faqs"/>
				</div>
			</div>
		</div>
	</transition>

	<WalkthroughDrawerPanel
		v-if="walkthroughData"
		ref="walkthroughDrawer"
		:walkthroughData="walkthroughData"
		@action-clicked="handleActionClicked"
		:slideKey="this.slideKey"
	/>

	<DrawerPanel
		ref="transactionsDrawer"
		bottom="-18px"
		openHeight="95%"
		closedHeight="0px"
		title="Recent Transactions"
		backgroundColor="white"
		openBackgroundColor="white"
	>
		<TransactionsList
			v-if="filteredTransactions && filteredTransactions.length > 0"
			@refreshTransaction="(t) => { refreshTransaction(t); }"
			@cleared="this.transactions = bankInfo.recentTransactions"
			@searchBack="(query) => { searchBackTransactions(query); }"
			:allowSearchBack="true"
			:showDateTotal="false"
			:search="true"
			:transactions="filteredTransactions"
			:formatter="formatter"
			:today="today"
			:yesterday="yesterday"
			:dividingLines="true"
		/>
	</DrawerPanel>

	<DrawerPanel
		ref="addAccountDrawer"
		:title="drawerTitle"
		bottom="-95px"
		:blurBackground="true"
		:openHeight="openHeight"
		closedHeight="0px"
		:startOpened="false"
		openBackgroundColor="white"
		transitionDuration=".5s"
		:divider="false"
		:hideNavBar="true"
		:back="isOfflineMode"
		@closed="handleDrawerClosed"
		@goBack="handleOfflineBack"
	>
		<div class="drawer-account-type-grid-wrapper">
			<div v-if="!isSpecificAccountMode && drawerOpen">
				<AccountTypeGrid
					:currentAccounts="currentAccounts"
					:creditAccounts="creditAccounts"
					:savingAccounts="savingAccounts"
					:insuranceInfo="insuranceInfo"
					:cols="1"
					:showAddButton="false"
					:filter="drawerFilter"
					@account-type-click="handleStartWalkthroughClick"
					@alt-action="handleAltAction"
				/>
				<div v-if="drawerFilter" class="view-all-link" @click="handleViewAllAccounts">
					<img :src="require('@/assets/arrow-left.svg')" class="view-all-arrow" />
					<span>View all</span>
				</div>
			</div>
			<div v-else-if="isOfflineMode" class="offline-account-list">
					<AccountTypeGrid
						:cols="1"
						:showAddButton="false"
						:offline="true"
					/>
			</div>
			<div v-else class="specific-account-content">
					<p class="specific-account-text">You can bring your current, credit & savings accounts into Money Guided in two secure ways:</p>
					<img src="@/assets/addCurrentAccountBenefits.png" alt="Add Current Account Benefits" class="account-benefits-image" />

					<div class="connect-buttons-wrapper">
						<MGButton
							:fullWidth="true"
							:noCaps="true"
							:text="`Connect with OPEN BANKING`"
							:arrow="true"
							:icon="require('@/assets/open-banking-bg.png')"
							@click="handleLinkAccount"
						/>
					</div>
					<MGButton
						:fullWidth="true"
						:noCaps="true"
						:text="`Add an offline account`"
						:stacked="true"
						:arrow="true"
						:alt2="true"
						:icon="require('@/assets/offlineAccount.png')"
						@click="handleLinkOfflineAccount"
					/>
					
					<MGButton
						:fullWidth="true"
						:noCaps="true"
						:text="`Add a different account`"
						:stacked="true"
						:textOnly="true"
						:icon="require('@/assets/arrow-left.png')"
						:leftIconSize="20"
						@click="handleAddDifferentAccount"
					/>
			</div>
		</div>
	</DrawerPanel>

	<WhatsAppEnableDrawer
		ref="whatsAppEnableDrawer"
		@enable="handleEnableWhatsApp"
		@done="handleWhatsAppEnableDone"
		@closed="handleWhatsAppDrawerClosed"
	/>
</template>

<script>
import Services from '../mixins/Services';
import WhatsAppMixin from '../mixins/WhatsAppMixin';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';
import AccountTypeGrid from './elements/AccountTypeGrid.vue';
import WalkthroughDrawerPanel from './elements/WalkthroughDrawerPanel.vue';
import DrawerPanel from './elements/DrawerPanel.vue';
import FaqsSection from './FaqsSection.vue';
import MGWalkthrough from './elements/MGWalkthrough.vue';
import AccountTypeFilter from './elements/AccountTypeFilter.vue';
import AccountCardsSection from './elements/AccountCardsSection.vue';
import AwaitBankAccountPanel from './elements/AwaitBankAccountPanel.vue';
import RecentTransactionsSection from './elements/RecentTransactionsSection.vue';
import TransactionsList from './elements/TransactionsList.vue';
import DocumentListItem from './elements/DocumentListItem.vue';
import WhatsAppPromoCard from './elements/WhatsAppPromoCard.vue';
import WhatsAppEnableDrawer from './elements/WhatsAppEnableDrawer.vue';

export default {
	name: 'MoneyPage',
	mixins: [Services, WhatsAppMixin],
	components: {
		CustomerLoadWait,
		HeaderBlock,
		MGButton,
		AccountTypeGrid,
		WalkthroughDrawerPanel,
		DrawerPanel,
		FaqsSection,
		MGWalkthrough,
		AccountTypeFilter,
		AccountCardsSection,
		AwaitBankAccountPanel,
		RecentTransactionsSection,
		TransactionsList,
		DocumentListItem,
		WhatsAppPromoCard,
		WhatsAppEnableDrawer
	},
	data() {
		return {
			currentSlide: 0,
			walkthroughData: null,
			slideKey: null,
			heroWalkthroughData: null,
			heroCarouselReady: false,
			currentAccounts: [],
			currentAccountsBalance: 0,
			creditAccounts: [],
			creditAccountsBalance: 0,
			savingAccounts: [],
			savingAccountsBalance: 0,
			selectedSection: 'all',
			selectedSubSection: 'all',
			adding:false,
			bankInfoLoaded: false,
			insuranceInfoLoaded: false,
			mortgageInfoLoaded: false,
			investmentInfoLoaded: false,
			creditInfoLoaded: false,
			documentsLoaded: false,
			additionalLongTerm: [],
			additionalInsurance: [],
			drawerTitle: 'Add account',
			isSpecificAccountMode: false,
			isOfflineMode: false,
			prevDrawerTitle: '',
			drawerFilter: null,
			faqs: [],
			drawerOpen: false,
			loadingWalkthrough: false,
			relinkNeeded: false
		}
	},
	computed: {
		navigationTabs() {
			const tabs = [{ value: 'all', label: 'All' }];

			// Day-to-day tab - only show subTabs if there are bank accounts
			const d2dTab = { value: 'd2d', label: 'Day-to-day' };
			if (this.hasBankAccounts) {
				const d2dSubs = [
					{ value: 'current', label: 'Current', hasData: this.currentAccounts?.length > 0 },
					{ value: 'credit', label: 'Credit', hasData: this.creditAccounts?.length > 0 },
					{ value: 'savings', label: 'Savings', hasData: this.savingAccounts?.length > 0 }
				];
				d2dSubs.sort((a, b) => (b.hasData ? 1 : 0) - (a.hasData ? 1 : 0));
				d2dTab.subTabs = [{ value: 'all', label: 'All' }, ...d2dSubs];
			}
			tabs.push(d2dTab);

			// Long-term tab - only show subTabs if there are long-term accounts
			const ltTab = { value: 'lt', label: 'Long-term' };
			const hasLongTermAccounts = this.mortgageInfo?.length > 0 ||
				this.creditInfo?.length > 0 ||
				this.investmentInfo?.length > 0;
			if (hasLongTermAccounts) {
				const ltSubs = [
					{ value: 'mortgages', label: 'Mortgages', hasData: this.mortgageInfo?.length > 0 },
					{ value: 'loans', label: 'Loans', hasData: this.creditInfo?.length > 0 },
					{ value: 'pensions', label: 'Pensions', hasData: this.investmentInfo?.some(a => a.accountType?.name === 'Pension') },
					{ value: 'investments', label: 'Investments', hasData: this.investmentInfo?.some(a => a.accountType?.name === 'Investment') }
				];
				ltSubs.sort((a, b) => (b.hasData ? 1 : 0) - (a.hasData ? 1 : 0));
				ltTab.subTabs = [{ value: 'all', label: 'All' }, ...ltSubs];
			}
			tabs.push(ltTab);

			// Insurances tab (no subTabs)
			tabs.push({ value: 'ins', label: 'Insurance' });

			// Documents tab (no subTabs)
			tabs.push({ value: 'docs', label: 'Documents' });

			return tabs;
		},
		showCurrentCard() {
			// Show if "All" is selected
			if (this.selectedSection === 'all') return true;

			// Show if "Day-to-day" is selected
			if (this.selectedSection === 'd2d') {
				// Show for "All" sub-tab or "Current" sub-tab
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'current';
			}

			return false;
		},
		showCreditCard() {
			if (this.selectedSection === 'all') return true;

			if (this.selectedSection === 'd2d') {
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'credit';
			}

			return false;
		},
		showSavingCard() {
			if (this.selectedSection === 'all') return true;

			if (this.selectedSection === 'd2d') {
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'savings';
			}

			return false;
		},
		showMortgageCard() {
			if (this.selectedSection === 'all') return true;

			if (this.selectedSection === 'lt') {
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'mortgages';
			}

			return false;
		},
		showPensionCard() {
			if (this.selectedSection === 'all') return true;

			if (this.selectedSection === 'lt') {
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'pensions';
			}

			return false;
		},
		showInvestmentCard() {
			if (this.selectedSection === 'all') return true;

			if (this.selectedSection === 'lt') {
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'investments';
			}

			return false;
		},
		showCreditInfo() {
			if (this.selectedSection === 'all') return true;

			if (this.selectedSection === 'lt') {
				return this.selectedSubSection === 'all' || this.selectedSubSection === 'loans';
			}

			return false;
		},
		showInsuranceCard() {
			return this.selectedSection === 'all' || this.selectedSection === 'ins';
		},
		hasBankAccounts() {
			return this.currentAccounts.length > 0 ||
					this.creditAccounts.length > 0 ||
					this.savingAccounts.length > 0;
		},
		hasAnyAccounts() {
			return this.currentAccounts?.length > 0 ||
					this.creditAccounts?.length > 0 ||
					this.savingAccounts?.length > 0 ||
					this.mortgageInfo?.length > 0 ||
					this.insuranceInfo?.length > 0 ||
					this.investmentInfo?.length > 0 ||
					this.creditInfo?.length > 0;
		},
		allAccountsLoaded() {
			return this.bankInfoLoaded && this.insuranceInfoLoaded && this.mortgageInfoLoaded && this.investmentInfoLoaded && this.creditInfoLoaded && this.documentsLoaded;
		},
		openHeight() {
			if (this.isSpecificAccountMode || this.isOfflineMode) {
				return '95%';
			}
			if (this.drawerFilter === 'd2d') {
				return '480px';
			}
			if (this.drawerFilter === 'lt') {
				return '560px';
			}
			return '95%';
		},
		filteredTransactions() {
			if (!this.transactions || this.selectedSection !== 'd2d' || this.selectedSubSection === 'all') {
				return this.transactions;
			}

			const accountIds = this.getAccountIdsByType(this.selectedSubSection);
			if (!accountIds || accountIds.length === 0) {
				return [];
			}

			return this.transactions.map(group => {
				return group.filter(t => accountIds.includes(t.bankAccountID));
			}).filter(group => group.length > 0);
		},
		getRecentTransactionsAccountType() {
			// all/all or d2d/all → show all transactions
			if (this.selectedSection === 'all' || this.selectedSubSection === 'all') {
				return 'all';
			}
			// d2d/current, d2d/credit, d2d/savings → show specific type
			return this.selectedSubSection;
		},
		filteredDocuments() {
			if (!this.documents) return [];
			return this.documents;
		},
		filteredCreditInfo() {
			if (!this.creditInfo) return [];
			if (!this.mortgageInfo || this.mortgageInfo.length === 0) return this.creditInfo;
			// Get IDs of credit accounts that are mortgages
			const mortgageAccountIds = this.mortgageInfo
				.map(m => m.account?.id)
				.filter(id => id);
			// Filter out credit accounts that are mortgages
			return this.creditInfo.filter(c => !mortgageAccountIds.includes(c.id));
		}
	},
	watch: {
		selectedSection(newVal) {
			// Only reset subSection to 'all' when explicitly selecting the 'all' tab
			if (newVal === 'all') {
				this.selectedSubSection = 'all';
			}
			// Persist to localStorage
			localStorage.setItem('money-filter-section', newVal);
		},
		selectedSubSection(newVal) {
			// Persist to localStorage
			localStorage.setItem('money-filter-subsection', newVal);
		}
	},
	methods: {
		getAccountIdsByType(type) {
			if (!this.bankInfo || !this.bankInfo.accounts) {
				return [];
			}

			// Return all bank account IDs when 'all' is selected
			if (type === 'all') {
				return this.bankInfo.accounts.map(a => a.id);
			}

			const accountTypeMap = {
				'current': 'Current',
				'credit': 'Credit',
				'savings': 'Savings'
			};

			const prettyName = accountTypeMap[type];
			if (!prettyName) {
				return [];
			}

			return this.bankInfo.accounts
				.filter(a => a.accountType?.prettyName === prettyName)
				.map(a => a.id);
		},
		onLoad: async function() {
			this._getCustomer(() => {
				this.getBankInfo(() => {
					this.bankInfoLoaded = true;
				});
				this._getInsuranceInfo(() => {
					this.insuranceInfoLoaded = true;
				});
				this._getMortgageInfo(() => {
					this.mortgageInfoLoaded = true;
				});
				this._getInvestmentInfo(() => {
					this.investmentInfoLoaded = true;
				});
				this._getCreditInfo(() => {
					this.creditInfoLoaded = true;
				});
				this._getDocuments(() => {
					this.documents.forEach((doc) => {
						if (doc.sysoDocumentType) {
							if (doc.sysoDocumentType.aiopSection == "insurance") { this.additionalInsurance.push(doc);}
							if (doc.sysoDocumentType.aiopSection == "longterm") { this.additionalLongTerm.push(doc);}
						}
					});
					this.documentsLoaded = true;
				});
			});
			this._getWalkthrough('money-hero', (result) => {
				this.heroWalkthroughData = result;
			});
			this._getFaqs('money-faqs',(result) => {
				this.faqs = result?.faqItems || [];
			});
		},
		getBankInfo: function(onSuccess = null){
			this._getBankingInfo(() => {
				this.currentAccounts = [];
				this.currentAccountsBalance = 0;
				this.creditAccounts = [];
				this.creditAccountsBalance = 0;
				this.savingAccounts = [];
				this.savingAccountsBalance = 0;
				this.bankInfo.accounts.forEach((a) =>
				{
					if (a.consentExpired) {
						this.relinkNeeded = true;
					}
					if (a.accountType.prettyName == "Current")
					{
						this.currentAccounts.push(a);
						this.currentAccountsBalance += a.balance;
					}
					else if (a.accountType.prettyName == "Credit")
					{
						this.creditAccounts.push(a);
						this.creditAccountsBalance += a.balance;
					}
					else if (a.accountType.prettyName == "Savings")
					{
						this.savingAccounts.push(a);
						this.savingAccountsBalance += a.balance;
					}
				});
				this.transactions = this.bankInfo.recentTransactions;
				this.loadVouchersForTransactions();
				if (onSuccess) { onSuccess(); }
			});
		},
		//TO DO:  When my PR has been merged - then this won't be required, as the 'availableDiscount' will be present
		// on a transaction if there is one
		loadVouchersForTransactions() {
			if (!this.transactions) return;

			// Fetch all vouchers in a single API call
			this._getAvailableVouchers(() => {
				if (!this.availableVouchers || this.availableVouchers.length === 0) return;

				// Create a map of SYSO merchant ID to voucher
				const vouchersByMerchant = {};
				this.availableVouchers.forEach(voucher => {
					if (voucher.merchant && voucher.merchant.sysoMerchantId) {
						if (!vouchersByMerchant[voucher.merchant.sysoMerchantId]) {
							vouchersByMerchant[voucher.merchant.sysoMerchantId] = voucher;
						}
					}
				});

				// Match vouchers to transactions locally
				this.transactions.forEach(group => {
					group.forEach(transaction => {
						if (transaction.merchant && transaction.merchant.id) {
							const voucher = vouchersByMerchant[transaction.merchant.id];
							if (voucher) {
								transaction.availableVoucher = voucher;
							}
						}
					});
				});

				// Trigger reactivity by reassigning array
				this.transactions = [...this.transactions];
			});
		},
		showWalkthrough() {
			const noBankAccounts = this.currentAccounts.length == 0 && this.creditAccounts.length == 0 && this.savingAccounts.length == 0;
			return noBankAccounts && (!this.hasAnyAccounts || this.selectedSection === 'all');
		},
		handleHeroSlideChange(index) {
			this.currentSlide = index;
		},
		handleLinkAccount() {
			// close the addaccount drawer if open
			if (this.$refs.addAccountDrawer) {
				this.$refs.addAccountDrawer.closeDrawer();
			}
			document.body.classList.remove('hide-nav-bar');
			this.adding = true;
			var navTo = this._getOpenBankingLinkUrl(this.customer.id);
			window.open(navTo, '_blank');
		},
		handleLinkOfflineAccount() {
			this.isOfflineMode = true;
			this.prevDrawerTitle = this.drawerTitle;
			this.drawerTitle = 'Add Offline Account';
		},
		handleOfflineBack() {
			this.isOfflineMode = false;
			this.drawerTitle = this.prevDrawerTitle || 'Add Account';
		},
		handleAddDifferentAccount() {
			this.isSpecificAccountMode = false;
		},
		handleHowItWorks() {
			this.$router.push('/story/money-how-it-works');
		},
		handleStartWalkthroughClick(accountType) {
			// Guard against double calls
			if (this.loadingWalkthrough) return;
			this.loadingWalkthrough = true;

			this._getWalkthrough(accountType.walkthrough, (result) => {
				this.loadingWalkthrough = false;
				this.slideKey = accountType.key;
				this.walkthroughData = result;
				this.$nextTick(() => {
					// Close the add account drawer first
					if (this.$refs.addAccountDrawer) {
						this.$refs.addAccountDrawer.closeDrawer();
					}
					// Then open the walkthrough drawer
					if (this.$refs.walkthroughDrawer) {
						this.$refs.walkthroughDrawer.openDrawer();
					}
				});
			});
		},
		handleAddAccountClick(section = null) {
			// D2D sub-sections go straight to specific account mode
			const d2dSubSectionLabels = {
				'current': 'current',
				'credit': 'credit card',
				'savings': 'savings'
			};
			if (d2dSubSectionLabels[section]) {
				this.drawerTitle = `Add a ${d2dSubSectionLabels[section]} account`;
				this.isSpecificAccountMode = true;
				this.drawerOpen = true;
				this.$nextTick(() => {
					if (this.$refs.addAccountDrawer) {
						this.$refs.addAccountDrawer.openDrawer();
					}
				});
				return;
			}

			// LT sub-sections go straight to the walkthrough for that account type
			const ltSubSectionKeys = {
				'mortgages': 'mortgage',
				'loans': 'loans',
				'pensions': 'pension',
				'investments': 'investments'
			};
			if (ltSubSectionKeys[section]) {
				const key = ltSubSectionKeys[section];
				const accountType = { walkthrough: 'money-account-types', key };
				this.handleStartWalkthroughClick(accountType);
				return;
			}

			this.drawerFilter = section;
			if (section === 'lt') {
				this.drawerTitle = 'Add a long-term account';
			} else if (section === 'd2d') {
				this.drawerTitle = 'Add a day-to-day account';
			} else {
				this.drawerTitle = 'Add account';
			}
			this.drawerOpen = true;
			this.$nextTick(() => {
				if (this.$refs.addAccountDrawer) {
					this.$refs.addAccountDrawer.openDrawer();
				}
			});
		},
		handleActionClicked(actionUri) {
			if (actionUri === '*truelayer*') {
				if (this.$refs.walkthroughDrawer) {
					this.$refs.walkthroughDrawer.closeDrawer();
				}
				this.drawerTitle = `Add a ${this.slideKey || ''} account`;
				this.isSpecificAccountMode = true;
				this.drawerOpen = true;
				this.$nextTick(() => {
					if (this.$refs.addAccountDrawer) {
						this.$refs.addAccountDrawer.openDrawer();
					}
				});
				return;
			}
			setTimeout(() => {
				this.$router.push(actionUri);
			}, 300);
		},
		handleMortgageClick() {
			this.$router.push('/money');
		},
		handleFilterChanged(filterObj) {
			if (filterObj.subSection !== undefined) {
				// Set section first
				this.selectedSection = filterObj.section;
				// Then set subSection after a tick to let AccountTypeFilter process
				this.$nextTick(() => {
					this.selectedSubSection = filterObj.subSection;
				});
			} else {
				this.selectedSection = filterObj.section;
			}
			window.scrollTo(0, 0);
		},
		handleAltAction(accountType) {
			const accountTypeName = accountType.label.replace(' Account', '').replace(' Card', '').replace(' Policy', '').toLowerCase();
			this.drawerTitle = `Add a ${accountTypeName} account`;
			this.isSpecificAccountMode = true;
		},
		handleDrawerClosed() {
			this.drawerTitle = 'Add account';
			this.isSpecificAccountMode = false;
			this.isOfflineMode = false;
			this.drawerFilter = null;
			this.drawerOpen = false;
			document.body.classList.remove('hide-nav-bar');
		},
		handleViewAllAccounts() {
			this.drawerFilter = null;
			this.drawerTitle = 'Add account';
		},
		determineCarouselHeight() {
			if (window.innerHeight < 700) {
				return '150px';
			} else {
				return '240px';
			}
		},
		openTransactionsDrawer() {
			this.$nextTick(() => {
				if (this.$refs.transactionsDrawer) {
					this.$refs.transactionsDrawer.openDrawer();
				}
			});
		},
		searchBackTransactions(query) {
			this._searchTransactions(query, (result) => {
				this.transactions = result;
			});
		},
		refreshTransaction(transactionId) {
			this._getTransaction(transactionId, (transaction) => {
				if (this.transactions) {
					this.transactions.forEach(group => {
						const index = group.findIndex(t => t.id === transaction.id);
						if (index !== -1) {
							group.splice(index, 1, transaction);
						}
					});
				}
			});
		},
		handleDocumentClick(doc) {
			this.$router.push('/documents/' + doc.id);
		},
		resetFilters() {
			this.selectedSection = 'all';
			this.selectedSubSection = 'all';
		},
	},
	mounted: function() {
		document.body.classList.remove('hide-nav-bar');

		// Restore filter selection from localStorage
		const savedSection = localStorage.getItem('money-filter-section');
		const savedSubSection = localStorage.getItem('money-filter-subsection');
		if (savedSection) {
			this.selectedSection = savedSection;
			if (savedSubSection) {
				this.selectedSubSection = savedSubSection;
			}
		}

		// Read query params from actual URL
		const hashQuery = window.location.hash.split('?')[1];
		const params = hashQuery ? new URLSearchParams(hashQuery) : null;

		// Only act on params if not already consumed in this history entry
		if (params && !window.history.state?.moneyParamsConsumed) {
			window.history.replaceState({ ...window.history.state, moneyParamsConsumed: true }, '');

			if (params.get('filter')) {
				this.selectedSection = params.get('filter');
			}
			if (params.get('show') === 'true') {
				setTimeout(() => {
					if (this.$refs.transactionsDrawer) {
						this.$refs.transactionsDrawer.openDrawer();
					}
				}, 500);
			}
			if (params.get('add') === 'true') {
				this.isSpecificAccountMode = true;
				this.drawerOpen = true;
				setTimeout(() => {
					if (this.$refs.addAccountDrawer) {
						this.$refs.addAccountDrawer.openDrawer();
					}
				}, 500);
			}
		}

		// Listen for reset event from MenuBar
		window.addEventListener('reset-money-filters', this.resetFilters);

		this.onLoad();
		// Delay hero carousel render to allow proper layout calculation on mobile
		setTimeout(() => {
			this.heroCarouselReady = true;
		}, 50);
	},
	beforeUnmount() {
		document.body.classList.remove('hide-nav-bar');
		window.removeEventListener('reset-money-filters', this.resetFilters);
	}
}
</script>

<style scoped>
.money-page {
	margin-left: -20px;
	margin-right: -20px;
	overflow-x: hidden;
	max-width: 100vw;
}

.connect-buttons-wrapper {
	margin-left: -20px;
	margin-right: -20px;
	width: calc(100% + 40px);
}

.how-it-works-button {
	/* margin-left: -20px;
	margin-right: -20px; */
	margin-top: -15px;
	margin-bottom: 24px;
}

/* Video Carousel Section */
.video-carousel-section {
	margin-bottom: 32px;
	margin-top: -20px;
	width: 100%;
	padding: 0;
}

/* Video Carousel Section uses MGWalkthrough which handles styling internally */

/* Tabbed Navigation */
.money-tabs {
	margin-bottom: 24px;
}

/* Content Section */
.content-section {
	padding: 0 20px;
}

.section-heading {
	font-size: 18px;
	font-weight: bold;
	margin-bottom: 12px;
	text-align: center;
}

.section-description {
	font-size: 13px;
	margin-bottom: 24px;
	text-align: center;
	height: 70px;
}

.primary-button {
	margin-bottom: 12px;
}

.what-can-add-heading {
	font-size: 13px;
	margin-top: 24px;
	margin-bottom: 8px;
	text-align: center;
	background: white;
	border-radius: 100px;
	padding: 12px 24px;
	margin-left: auto;
	margin-right: auto;
	width: fit-content;
	position: relative;
}

.what-can-add-heading::after {
	content: '';
	position: absolute;
	bottom: -8px;
	left: 50%;
	transform: translateX(-50%);
	width: 0;
	height: 0;
	border-left: 8px solid transparent;
	border-right: 8px solid transparent;
	border-top: 8px solid white;
}

.what-can-add-subtext {
	font-size: 13px;
	text-align: center;
	margin-top: 18px;
}

.account-type-grid-section {
	margin-top: 24px;
}

.offline-account-list {
	display: flex;
	flex-direction: column;
}

.faq-section {
	border-radius: 8px;
	background-color: white;
}

.drawer-account-type-grid-wrapper {
	padding: 0 20px;
}

.view-all-link {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 16px 0;
	font-size: 13px;
	color: #413655;
}

.view-all-arrow {
	width: 16px;
	height: 12px;
}

.specific-account-content {
	padding: 20px 0;
	display: flex;
	flex-direction: column;
}

.specific-account-text {
	font-size: 13px;
	line-height: 20px;
	text-align: center;
	margin: -20px -20px 20px -20px;
}

.account-benefits-image {
	width: calc(100% + 40px);
	max-width: calc(100% + 40px);
	height: auto;
	display: block;
	margin-left: -20px;
	margin-right: -20px;
}

.documents-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.empty-documents {
	text-align: center;
	padding: 0 20px;
}

.empty-state-text {
	font-size: 18px;
	font-weight: bold;
	margin: 0 0 8px 0;
}

.empty-state-subtext {
	font-size: 14px;
	color: #666;
	margin: 0;
}

.whatsapp-promo-section {
	margin-top: 24px;
}
</style>
